import React, { useEffect, useState } from 'react';
import { Check, Search } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useUser } from '../../../contexts/UserContext';
import PrivateLayout from '../../layouts/PrivateLayout';
import { useFetchReviewQueue } from '../../../services/partners';

import PartnersTable from './PartnersTable';
import PartnerReviewQueue from './PartnerReviewQueue';
import { canViewSignedPaperwork } from './partnerHelpers';

// Operations > Partners hub: INDIVIDUAL referral partners only (directory +
// review queue). Organizations (API lead partners like Envoy, plus the internal
// "Referral Program" umbrella) are deliberately not shown or creatable here —
// they're rare, managed via SQL + the set-partner-api-key script, and live a
// completely different life (leads board / API keys, no QR/paperwork/payouts).
const PartnersPage = () => {
  const { hasPermission } = useUser();
  // partners:view grants the page (read-only); partners:manage unlocks all edit controls.
  const canManage = hasPermission('partners:manage');
  const canViewPaperwork = canViewSignedPaperwork(hasPermission);

  const [tab, setTab] = useState('partners'); // 'partners' | 'review'
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Page-1 fetch doubles as the tab badge count; the queue component's own
  // page-1 query shares this cache entry, so it costs nothing extra.
  const { data: queueData } = useFetchReviewQueue(1);
  const queueTotal = queueData?.total || 0;

  return (
    <PrivateLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Partners</h1>
          <p className="text-sm text-muted-foreground">
            Referral partner organizations and individual referrers, plus new
            applications waiting for review.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Chip-style tabs (Operations filter-chip recipe) */}
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setTab('partners')}
              className={cn(
                'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                tab === 'partners'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              Partners
            </button>
            <button
              type="button"
              onClick={() => setTab('review')}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                tab === 'review'
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              Review Queue
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-semibold leading-none',
                  queueTotal > 0
                    ? 'bg-status-warning-bg text-status-warning-fg'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {queueTotal}
              </span>
            </button>
          </div>

          {tab === 'partners' ? (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search partners…"
                  className="flex h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <button
                type="button"
                onClick={() => setIncludeInactive((v) => !v)}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    includeInactive
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border',
                  )}
                >
                  {includeInactive ? <Check className="h-3 w-3" /> : null}
                </span>
                Include inactive
              </button>
            </div>
          ) : null}
        </div>

        {tab === 'partners' ? (
          <PartnersTable
            search={search}
            includeInactive={includeInactive}
            canManage={canManage}
            canViewPaperwork={canViewPaperwork}
          />
        ) : (
          <PartnerReviewQueue
            canManage={canManage}
            canViewPaperwork={canViewPaperwork}
          />
        )}
      </div>

    </PrivateLayout>
  );
};

export default PartnersPage;
